import React, { useEffect, useState, Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { CouponModal } from '../components/modals/CouponModal';
import { SectionSkeleton } from '../components/shared/SkeletonLoader';
import { TicketIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { OffersHeroBanner } from '../components/offers/OffersHeroBanner';
import { TopOffers } from '../components/home/TopOffers';
import { ShopByCategory } from '../components/home/ShopByCategory';
import { OffersBannerRow } from '../components/offers/OffersBannerRow';
import { OffersFullWidthBanner } from '../components/offers/OffersFullWidthBanner';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { PullToRefresh } from '../components/shared/PullToRefresh';
const FlashSales = lazy(() => import('../components/home/FlashSales').then(m => ({
  default: m.FlashSales
})));
const DealOfTheDay = lazy(() => import('../components/home/DealOfTheDay').then(m => ({
  default: m.DealOfTheDay
})));
const TrendingProducts = lazy(() => import('../components/home/TrendingProducts').then(m => ({
  default: m.TrendingProducts
})));
const ExclusiveDeals = lazy(() => import('../components/home/ExclusiveDeals').then(m => ({
  default: m.ExclusiveDeals
})));
const EnhancedClearanceSection = lazy(() => import('../components/home/EnhancedClearanceSection').then(m => ({
  default: m.EnhancedClearanceSection
})));
const tabs: {
  id: string;
  label: string;
  sectionId?: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'flash',
  label: '⚡ Flash',
  sectionId: 'flash-sales'
}, {
  id: 'deal-of-day',
  label: '🔥 Deal of Day',
  sectionId: 'deal-of-day'
}, {
  id: 'trending',
  label: '📈 Trending',
  sectionId: 'trending-now'
}, {
  id: 'exclusive',
  label: '💎 Exclusive',
  sectionId: 'exclusive-deals'
}, {
  id: 'clearance',
  label: '🏷️ Clearance',
  sectionId: 'clearance-sale'
}];
export function OffersPage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const handleTabClick = (tab: (typeof tabs)[number]) => {
    setActiveTab(tab.id);
    if (tab.sectionId) {
      const el = document.getElementById(tab.sectionId);
      if (el) {
        const offset = window.innerWidth >= 768 ? 240 : 170;
        const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
        // brief highlight
        el.classList.add('ring-2', 'ring-[#fdc300]', 'ring-offset-2', 'rounded-xl');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-[#fdc300]', 'ring-offset-2', 'rounded-xl');
        }, 1500);
      }
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  // Update active tab based on which section is most visible
  useEffect(() => {
    const handler = () => {
      const sectionTabs = tabs.filter(t => t.sectionId);
      let current = 'all';
      const viewportMid = window.innerHeight / 2;
      for (const t of sectionTabs) {
        const el = document.getElementById(t.sectionId!);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= viewportMid && rect.bottom >= viewportMid) {
          current = t.id;
          break;
        }
      }
      setActiveTab(current);
    };
    window.addEventListener('scroll', handler, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  return <HelmetProvider data-id="element-7145" data-mp="2go92kg0y3jm9">
      <SEO title="Deals & Offers - Save Up to 70% on Health Products" description="Discover amazing deals and exclusive offers at Innova Pharmacy. Save up to 70% on health, beauty, and wellness products with fast delivery across Saudi Arabia." canonical="/en/offers/" robots="index, follow, max-image-preview:large" hreflangAr="/offers/" hreflangEn="/en/offers/" hreflangDefault="/offers/" ogImage="https://cdn.innovaksa.net/og/offers.jpg" keywords="pharmacy deals Saudi Arabia, health product offers, discount supplements, beauty sale, vitamins on sale, pharmacy promotions Riyadh, cheap medications, wellness deals" data-id="element-7146" data-mp="2gmo8ssmgg4pg" />
      <div className="flex flex-col min-h-screen bg-gray-50 overflow-x-hidden pb-32 md:pb-0" data-id="element-7147" data-mp="1du287chx8ylt">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-7148" data-mp="1isdgvjwhd32g">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement={true} data-id="element-7149" data-mp="1nl6n6c7lkjgn" />
        </div>
        {/* Mobile Header Overlay */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-7150" data-mp="21lr5765oub93">
          <MobileInternalHeader title="Deals & Offers" data-id="element-7151" data-mp="1i6r1hleayyi" />
        </div>

        <PullToRefresh onRefresh={async () => {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }} data-id="element-7152" data-mp="1gdj7yz1q8ox">
          <main className="flex-grow pt-0 md:pt-[var(--header-height-desktop)]" data-id="element-7153" data-mp="b8j7oyq3a7zd">
            <h1 className="sr-only" data-id="element-7154" data-mp="6ht15i5xfz33">
              Deals & Offers - Save Big on Health and Beauty Products
            </h1>
            <PageHeroBreadcrumb breadcrumbs={[{
            label: 'Home',
            href: '/'
          }, {
            label: 'Offers'
          }]} title="Deals & Offers" description="Discover amazing deals and exclusive offers at Innova Pharmacy. Save up to 70% on health, beauty, and wellness products with fast delivery across Saudi Arabia." data-id="element-7155" data-mp="k7wko1df9eou" />

            {/* Sticky Mobile Tab Bar — scrolls to section + tracks active */}
            <div className="md:hidden sticky top-[56px] z-40 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm" data-id="element-7156" data-mp="16hmv3cuni1a9">
              <div className="overflow-x-auto scrollbar-hide px-4 py-2.5" data-id="element-7157" data-mp="1bgprk8wkez6m">
                <div className="flex gap-2" data-id="element-7158" data-mp="wrth6571da2u">
                  {tabs.map(tab => <button key={tab.id} onClick={() => handleTabClick(tab)} className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${activeTab === tab.id ? 'bg-[#1E2B55] text-white shadow-sm' : 'bg-gray-100 text-gray-700 active:bg-gray-200'}`} data-id="element-7159" data-mp="11qmyk8pg1l8a">
                      {tab.label}
                    </button>)}
                </div>
              </div>
            </div>

            <section className="py-2 md:py-6" data-id="element-7160" data-mp="286ypbopxs0tu">
              <OffersHeroBanner data-id="element-7161" data-mp="2878e58z8w1xy" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7162" data-mp="2d6bmb2aiuoa5">
              <OffersBannerRow columns={6} bannerSpec={{
              width: 220,
              height: 220,
              ratio: '1:1'
            }} count={6} data-id="element-7163" data-mp="2d50ij2h2tgj1" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7164" data-mp="r5v9kbaui0om">
              <TopOffers data-id="element-7165" data-mp="r4k5099ksowg" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7166" data-mp="m8bvpci27px7">
              <OffersBannerRow columns={3} bannerSpec={{
              width: 440,
              height: 248,
              ratio: '16:9'
            }} count={3} data-id="element-7167" data-mp="m5g03utopdbx" />
            </section>
            <section id="flash-sales" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7168" data-mp="88u64uwxw8ms">
              <Suspense fallback={<SectionSkeleton data-id="element-7170" data-mp="22ls9svo4w715" />} data-id="element-7169" data-mp="3asmo8vlq7as">
                <FlashSales data-id="element-7171" data-mp="22kqyqr74bkjf" />
              </Suspense>
            </section>
            <section className="py-1 md:py-4" data-id="element-7172" data-mp="1xmyzfc458w57">
              <OffersBannerRow columns={3} bannerSpec={{
              width: 360,
              height: 270,
              ratio: '4:3'
            }} count={6} data-id="element-7173" data-mp="1xn8ngu5mot89" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7174" data-mp="2evroy38zaalf">
              <ShopByCategory data-id="element-7175" data-mp="2ey4htbg9y5d9" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7176" data-mp="2jve4wkg6y4xx">
              <OffersBannerRow columns={4} bannerSpec={{
              width: 320,
              height: 240,
              ratio: '4:3'
            }} count={4} data-id="element-7177" data-mp="2jx79zvb5oh3t" />
            </section>
            <section id="deal-of-day" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7178" data-mp="1w4iiy3yf23yl">
              <Suspense fallback={<SectionSkeleton data-id="element-7180" data-mp="37kp6g45d83d4" />} data-id="element-7179" data-mp="212jzah4gjp6d">
                <DealOfTheDay data-id="element-7181" data-mp="37jnkh4mt9wgm" />
              </Suspense>
            </section>
            <section className="py-1 md:py-4" data-id="element-7182" data-mp="3cja9ebc05159">
              <OffersBannerRow columns={2} bannerSpec={{
              width: 680,
              height: 382,
              ratio: '16:9'
            }} count={2} data-id="element-7183" data-mp="3ci8ntcxd0w9a" />
            </section>
            <section id="trending-now" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7184" data-mp="1vra41avgit9u">
              <Suspense fallback={<SectionSkeleton data-id="element-7186" data-mp="20shbf8totqfz" />} data-id="element-7185" data-mp="20pv4nbemfeyt">
                <TrendingProducts data-id="element-7187" data-mp="140vyc1xlkn99" />
              </Suspense>
            </section>
            <section className="py-1 md:py-4" data-id="element-7188" data-mp="181pj4mfqtlxx">
              <OffersFullWidthBanner data-id="element-7189" data-mp="182hfhzh5znp9" />
            </section>
            <section id="exclusive-deals" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7190" data-mp="1d0iy6jaohl07">
              <Suspense fallback={<SectionSkeleton data-id="element-7192" data-mp="1hy2averq9pq1" />} data-id="element-7191" data-mp="1i1q5yictmk6v">
                <ExclusiveDeals data-id="element-7193" data-mp="2ogh9kttbwj4l" />
              </Suspense>
            </section>
            <section className="py-1 md:py-4" data-id="element-7194" data-mp="2oilhdmucm2a5">
              <OffersBannerRow columns={6} bannerSpec={{
              width: 260,
              height: 346,
              ratio: '3:4'
            }} count={6} data-id="element-7195" data-mp="2te95dyx3gpe2" />
            </section>
            <section id="clearance-sale" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7196" data-mp="81vrwzielxug">
              <Suspense fallback={<SectionSkeleton data-id="element-7198" data-mp="32sm3culnnlf" />} data-id="element-7197" data-mp="32atcq75bri9">
                <EnhancedClearanceSection data-id="element-7199" data-mp="3v6v3cq4jiddl" />
              </Suspense>
            </section>
          </main>
        </PullToRefresh>
        <Footer data-id="element-7200" data-mp="3q7ii29tbhed4" />
        <MobileFooterNav data-id="element-7201" data-mp="3q6z4146sfa4d" />
        <PharmacistChat data-id="element-7202" data-mp="3q9v1kreq3wsa" />
        <div className="h-20 md:hidden" data-id="element-7203" data-mp="3l8fektszjg79"></div>

        {/* Floating Apply Coupon CTA (Mobile Only) */}
        <button onClick={() => setCouponModalOpen(true)} className="md:hidden fixed bottom-[80px] left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-gradient-to-r from-[#fdc300] to-[#ffd84d] text-[#1E2B55] rounded-full shadow-xl px-5 py-3 font-bold text-sm active:scale-95 transition-transform" aria-label="Apply a coupon" data-id="element-7204" data-mp="3g9udyt9tmuia">
          <TicketIcon className="w-4 h-4" data-id="element-7205" data-mp="38wd0udsxab6n" />
          Apply Coupon
        </button>

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-7206" data-mp="3dqh70uud6f6o" />
        <CouponModal isOpen={couponModalOpen} onClose={() => setCouponModalOpen(false)} onApplyCoupon={code => {
        toast.success(`Coupon ${code} copied! Apply at checkout.`);
        setCouponModalOpen(false);
      }} data-id="element-7207" data-mp="3ronz1ucmtkyf" />
      </div>
    </HelmetProvider>;
}